import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { Search, History } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { auditAPI } from '../../services/auditAPI';
import toast from 'react-hot-toast';

function AuditLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    start_date: '',
    end_date: '',
    limit: 100
  });

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await auditAPI.getAll(filters);
      const logsData = Array.isArray(response?.data?.data) ? response.data.data : [];
      setLogs(logsData);
    } catch (error) {
      toast.error('Erro ao carregar logs de auditoria');
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    loadLogs();
  };

  const getActionColor = (action) => {
    if (action.includes('POST')) return 'success';
    if (action.includes('PUT')) return 'warning';
    if (action.includes('DELETE')) return 'error';
    return 'primary';
  };

  const getActionLabel = (action) => {
    const actionMap = {
      'POST': t('audit.actions.create'),
      'PUT': t('audit.actions.update'),
      'DELETE': t('audit.actions.delete'),
      'GET': t('audit.actions.read')
    };
    return actionMap[action] || action;
  };

  const getResourceTypeColor = (resourceType) => {
    switch (resourceType) {
      case 'equipment': return 'primary';
      case 'barriers': return 'secondary';
      case 'suppliers': return 'info';
      case 'devices': return 'warning';
      default: return 'default';
    }
  };

  const getResourceTypeLabel = (resourceType) => {
    const resourceMap = {
      'equipment': t('audit.resources.equipment'),
      'barriers': t('audit.resources.barriers'),
      'suppliers': t('audit.resources.suppliers'),
      'devices': t('audit.resources.devices'),
      'users': t('audit.resources.users'),
      'maps': t('audit.resources.maps')
    };
    return resourceMap[resourceType] || resourceType;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('pt-BR');
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <History sx={{ mr: 1 }} />
        <Typography variant="h4">
          Logs de Auditoria
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Ação</InputLabel>
              <Select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="POST">Criar</MenuItem>
                <MenuItem value="PUT">Atualizar</MenuItem>
                <MenuItem value="DELETE">Excluir</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Recurso</InputLabel>
              <Select
                value={filters.resource_type}
                onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="equipment">Equipamentos</MenuItem>
                <MenuItem value="barriers">Barreiras</MenuItem>
                <MenuItem value="suppliers">Fornecedores</MenuItem>
                <MenuItem value="devices">Dispositivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Limite"
              type="number"
              size="small"
              fullWidth
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
              disabled={loading}
              fullWidth
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Recurso</TableCell>
              <TableCell>ID do Recurso</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <Typography variant="body2">
                    {formatTimestamp(log.timestamp)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{log.user_email}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {log.user_id}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getActionLabel(log.action)}
                    color={getActionColor(log.action)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getResourceTypeLabel(log.resource_type)}
                    color={getResourceTypeColor(log.resource_type)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {log.resource_id || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    Status: {log.details?.status_code}
                  </Typography>
                  {log.details?.body && (
                    <Typography variant="caption" display="block">
                      {JSON.stringify(log.details.body).substring(0, 50)}...
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {logs.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary">
            Nenhum log de auditoria encontrado
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default AuditLogs;